import { IIndustryWithPolicyTypes, updateIndustry } from "@/api/industries";
import { Button } from "@/components/shared/buttons/Button";
import { Panel } from "@/components/shared/Panel";
import { Edit } from "@mui/icons-material";
import { Box, Stack, Typography } from "@mui/material";
import MDEditor from "@uiw/react-md-editor";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import rehypeSanitize from "rehype-sanitize";

interface Props {
    industry: IIndustryWithPolicyTypes;
}

export const IndustryRisksEditor = ({ industry }: Props) => {
    const [isEditing, setIsEditing] = useState(false);
    const [value, setValue] = useState(industry.insurance_risks || "");
    const queryClient = useQueryClient();

    const { mutate, isPending } = useMutation({
        mutationFn: () =>
            updateIndustry(industry.id, {
                insurance_risks: value.trim() || null,
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["admin-industry", String(industry.id)],
            });
            queryClient.invalidateQueries({ queryKey: ["admin-industries"] });
            setIsEditing(false);
        },
    });

    const handleCancel = () => {
        setValue(industry.insurance_risks || "");
        setIsEditing(false);
    };

    return (
        <Panel
            title="Insurance Risks"
            actions={
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Edit fontSize="small" />}
                    onClick={() => setIsEditing(true)}
                    sx={{
                        visibility: isEditing ? "hidden" : "visible",
                        color: (theme) => theme.palette.primary.contrastText,
                        borderColor: (theme) =>
                            theme.palette.primary.contrastText,
                        "&:hover": {
                            backgroundColor: (theme) =>
                                theme.palette.action.hover,
                        },
                        "& .MuiSvgIcon-root": {
                            fontSize: "1rem",
                        },
                    }}
                >
                    Edit
                </Button>
            }
        >
            {isEditing ? (
                <Stack spacing={2}>
                    <Box data-color-mode="light">
                        <MDEditor
                            value={value}
                            onChange={(val) => setValue(val || "")}
                            preview="edit"
                            previewOptions={{
                                rehypePlugins: [[rehypeSanitize]],
                            }}
                            minHeight={200}
                        />
                    </Box>
                    <Stack direction="row" gap={1} justifyContent="flex-end">
                        <Button
                            type="button"
                            variant="outlined"
                            size="small"
                            onClick={handleCancel}
                            disabled={isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            variant="contained"
                            size="small"
                            onClick={() => mutate()}
                            loading={isPending}
                        >
                            Save
                        </Button>
                    </Stack>
                </Stack>
            ) : industry.insurance_risks ? (
                <Box data-color-mode="light">
                    <MDEditor.Markdown
                        source={industry.insurance_risks}
                        rehypePlugins={[[rehypeSanitize]]}
                    />
                </Box>
            ) : (
                <Typography color="text.secondary">
                    No insurance risks defined
                </Typography>
            )}
        </Panel>
    );
};
